import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, apiError } from "@/lib/api";
import { Field, ErrorText, btnPrimary, btnSecondary, inputCls } from "@/components/portal/ui";

export default function StaffDialog({ open, onOpenChange, person, kind, specialties = [], onSaved }) {
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const isDoctor = kind === "doctor";

  useEffect(() => {
    if (open && person) {
      setForm({
        first_name: person.first_name || "",
        last_name: person.last_name || "",
        phone: person.phone || "",
        city: person.city || "",
        license_number: person.license_number || "",
        bio: person.bio || "",
        specialty_ids: person.specialty_ids || [],
      });
      setError("");
    }
  }, [open, person]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleSpec = (id, on) => setForm((f) => ({ ...f, specialty_ids: on ? [...f.specialty_ids, id] : f.specialty_ids.filter((x) => x !== id) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      if (!isDoctor) delete payload.specialty_ids;
      const { data } = await api.put(`/${isDoctor ? "doctors" : "nurses"}/${person.id}`, payload);
      toast.success("Profilo aggiornato");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!person) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-xl" data-testid="staff-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Modifica {isDoctor ? "medico" : "infermiere"}</DialogTitle>
          <DialogDescription>{person.email}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Nome" htmlFor="staff-first"><input id="staff-first" required value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid="staff-first-name-input" /></Field>
          <Field label="Cognome" htmlFor="staff-last"><input id="staff-last" required value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid="staff-last-name-input" /></Field>
          <Field label="Telefono" htmlFor="staff-phone"><input id="staff-phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="staff-phone-input" /></Field>
          <Field label="Città / zona" htmlFor="staff-city"><input id="staff-city" value={form.city} onChange={set("city")} className={inputCls} data-testid="staff-city-input" /></Field>
          <Field label={isDoctor ? "N. iscrizione Ordine" : "N. iscrizione OPI"} htmlFor="staff-license" className="sm:col-span-2">
            <input id="staff-license" value={form.license_number} onChange={set("license_number")} className={inputCls} data-testid="staff-license-input" />
          </Field>
          {isDoctor && (
            <>
              <Field label="Specialità" className="sm:col-span-2">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="staff-specialties">
                  {specialties.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 rounded-2xl bg-[#F6F4FA] px-3 py-2 text-sm">
                      <Checkbox checked={form.specialty_ids?.includes(s.id)} onCheckedChange={(v) => toggleSpec(s.id, !!v)} className="data-[state=checked]:bg-[#5B2C91] data-[state=checked]:border-[#5B2C91]" data-testid={`staff-specialty-${s.id}`} />
                      {s.name}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Bio" htmlFor="staff-bio" className="sm:col-span-2">
                <textarea id="staff-bio" rows={3} value={form.bio} onChange={set("bio")} className={inputCls} data-testid="staff-bio-input" />
              </Field>
            </>
          )}
          <div className="sm:col-span-2"><ErrorText testid="staff-form-error">{error}</ErrorText></div>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => onOpenChange(false)} className={btnSecondary} data-testid="staff-cancel-btn">Annulla</button>
            <button type="submit" disabled={saving} className={btnPrimary} data-testid="staff-save-btn">{saving ? "Salvataggio…" : "Salva"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
